import { measureText, Tooltip, unityTokens } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { GaugeChart as EChartsGaugeChart, GaugeSeriesOption } from 'echarts/charts';
import { GridComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { ContentDimensions, formatValue, UnitOptions } from '../../core';
import { useThrottledResizing } from '../../core/utils/resizing';
import { useChartsTheme } from '../context/ChartsProvider';
import { EChart } from '../EChart';
import { FillTextSpace } from '../utils/FillTextSpace';
import { useCorrectedAxisLine, useValueColorAxisLine } from './axisLineHooks';
import { GaugeSeries } from './types';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([EChartsGaugeChart, GridComponent, TitleComponent, TooltipComponent, CanvasRenderer]);

export type GaugeChartValue = number | null | undefined;

export interface GaugeChartBaseProps extends ContentDimensions {
  data: GaugeSeries;
  unit: UnitOptions;
  axisLine: GaugeSeriesOption['axisLine'];
  max?: number;
  /**
   * The message to be shown on the chart
   */
  message?: string;
  /**
   * Boolean to control whether the value should be shown on the chart
   * or if only a message should be shown
   */
  hideValue?: boolean;
}

const STATIC_OPTIONS: EChartsCoreOption = {
  animation: false,
  title: {
    show: false,
  },
  tooltip: {
    show: false,
  },
};

// Breakpoints used for responsively sizing arrow and progress width
const GAUGE_SMALL_BREAKPOINT = 170;
const GAUGE_EXTRA_SMALL_BREAKPOINT = 90;
const GAUGE_MAX_BREAKPOINT = 800;

// These are all constant values for sizing the gauge chart.
// Not calculated but rather decided off of how they look
const TITLE_HEIGHT = 12;
const THRESHOLDS_LINE_WIDTH = 5;
const SPACING_BETWEEN_THRESHOLDS_AND_PROGRESS = 10;
const MIN_POINTER_LENGTH = 8;
const MAX_POINTER_LENGTH = 16;

const MIN_PROGRESS_WIDTH = 16;
const MAX_PROGRESS_WIDTH = 32;

const SPACING_BETWEEN_PROGRESS_AND_POINTER = 5;
const SPACING_BETWEEN_MESSAGE_AND_TITLE = 8;
const MIN_VALUE_FONT_SIZE = 12;

/**
 * Use the context-aware `GaugeChart.Viz` instead.
 *
 * @deprecated
 */
export function GaugeChartBase(props: GaugeChartBaseProps) {
  const { width, height, data, unit, axisLine, max, message, hideValue } = props;
  const { panwRebrandStubYear } = useFeatureFlags();
  const chartsTheme = useChartsTheme();

  // Axis that will be just the color for the current value
  const valueColorAxisLine = useValueColorAxisLine(axisLine, data, max);
  const textColor = valueColorAxisLine?.lineStyle?.color?.[0]?.[1];
  // Axis line to use for radial axis, with any color corrections that we need
  const correctedAxisLine = useCorrectedAxisLine(axisLine);

  const { throttledWidth } = useThrottledResizing(
    { width, height },
    {
      // We use a very small throttle for this to avoid the font size updates looking
      // "choppy" as the user resizes.
      throttleDimensionsMs: 50,
    }
  );

  const messageToDisplay = message || '';
  const showValue = !hideValue || (hideValue === true && messageToDisplay === '');
  const valueToDisplay = data.value ?? null;

  // Strip newlines so ECharts `overflow: 'truncate'` works correctly.
  // PromQL queries can contain newlines which break single-line truncation.
  const sanitizedLabel = data.label?.replace(/[\r\n]+/g, ' ') ?? '';
  const formattedValue = data.value != null ? formatValue(data.value, unit) : 'null';
  const tooltipContent = (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', maxWidth: '650px' }}>
      {sanitizedLabel && <div style={{ fontWeight: 600, wordBreak: 'break-all', flex: 1 }}>{sanitizedLabel}</div>}
      <div style={{ whiteSpace: 'nowrap' }}>{formattedValue}</div>
    </div>
  );

  const {
    gaugeSize,
    messageBoxHeight,
    messageBoxWidth,
    messageBoxAbsoluteX,
    messageBoxAbsoluteY,
    pointerLength,
    pointerOffset,
    progressGaugeRadius,
    progressWidth,
    radius,
    titleVerticalCenter,
    valueBoxHeight,
    valueBoxWidth,
    valueBoxOffset,
    valueTruncated,
  } = useMemo(() => {
    return calculateGaugeLayout({
      width: throttledWidth,
      height,
      showValue,
      value: valueToDisplay,
      unit,
      panwRebrandStubYear,
    });
  }, [height, throttledWidth, unit, valueToDisplay, showValue, panwRebrandStubYear]);

  // TODO: see if we can reduce how often this gets re-run on resize when we
  // create the utils described in sc-75005
  // TODO: also see if we can reduce some of this getting updated by pulling the
  // data out into a dataset as noted in sc-80066
  const option: EChartsCoreOption = useMemo(() => {
    if (data.value === undefined) return chartsTheme.noDataOption;

    const BASE_SERIES_SETTINGS = {
      type: 'gauge',
      center: ['50%', '65%'],
      radius: radius,
      startAngle: 200,
      endAngle: -20,
      min: 0,
      max,
      silent: true,
      pointer: {
        show: true,
        icon: 'none',
      },
      axisLine: {
        ...valueColorAxisLine,
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
      axisLabel: {
        show: false,
      },
      anchor: {
        show: false,
      },
      title: {
        show: false,
      },
      detail: {
        show: false,
      },
      data: [
        {
          value: data.value,
        },
      ],
    };

    return {
      ...STATIC_OPTIONS,
      series: [
        // The empty progress gauge that is just the gray color
        // Also has the series name
        {
          ...BASE_SERIES_SETTINGS,
          max: 1,
          radius: progressGaugeRadius,
          progress: {
            show: gaugeSize !== 'extra-small',
            width: progressWidth,
            itemStyle: {
              color: '#E1E5E9',
            },
          },
          axisLine: { show: false },
          data: [
            {
              value: 1,
              itemStyle: { color: '#E1E5E9' },
              name: sanitizedLabel,
              title: {
                show: true,
                color: chartsTheme.echartsTheme.textStyle?.color ?? 'inherit', // series name font color
                offsetCenter: [0, titleVerticalCenter],
                overflow: 'truncate',
                fontSize: TITLE_HEIGHT,
                width: throttledWidth * 0.8,
              },
            },
          ],
          z: 0,
        },

        // The filled progress gauge.  Fill color matches the step color
        {
          ...BASE_SERIES_SETTINGS,
          max,
          radius: progressGaugeRadius,
          progress: {
            show: gaugeSize !== 'extra-small',
            width: progressWidth,
            itemStyle: {
              color: 'auto',
            },
          },
          data: [{ value: data.value, itemStyle: { color: 'auto' } }],
          z: 2,
        },

        // The main text and pointer arrow
        {
          ...BASE_SERIES_SETTINGS,
          radius: pointerOffset,
          z: 1,
          detail: {
            overflow: valueTruncated ? 'truncate' : 'none',
            show: showValue,
            width: valueBoxWidth,
            padding: [0, 0, 0, 0],
            height: valueBoxHeight,
            offsetCenter: [0, valueBoxOffset * -1], // make it negative to move it up
            color: 'inherit', // allows value color to match active threshold color
            fontSize: valueBoxHeight,
            formatter:
              data.value === null
                ? // We use a different function when we *know* the value is null
                  // at this level because the `formatter` function argument is `NaN`
                  // when the value is `null`, making it difficult to differentiate
                  // `null` from a true `NaN` case.
                  () => 'null'
                : (value: number) => {
                    if (typeof valueToDisplay === 'string') {
                      return valueToDisplay;
                    }

                    return formatValue(value, unit);
                  },
          },
          pointer: {
            show: true,
            // pointer hidden for small panels, path taken from ex: https://echarts.apache.org/examples/en/editor.html?c=gauge-grade
            icon: gaugeSize !== 'extra-small' ? 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z' : 'none',
            length: pointerLength,
            width: pointerLength / 2,
            offsetCenter: [0, -1 * pointerOffset],
            itemStyle: {
              color: 'auto',
            },
          },
          data: [
            {
              value: data.value,
              itemStyle: { color: 'auto' },
            },
          ],
        },

        // The radial that shows all the breakpoints
        {
          ...BASE_SERIES_SETTINGS,
          z: 3,
          // this uses the corrected axis line so that break points are properly shown
          // hidden at small breakpoint since arcs are too small to be useful
          ...(gaugeSize === 'default'
            ? {
                axisLine: {
                  ...correctedAxisLine,
                  lineStyle: { ...correctedAxisLine?.lineStyle, width: THRESHOLDS_LINE_WIDTH },
                },
              }
            : {}),
        },
      ],
    };
  }, [
    gaugeSize,
    radius,
    pointerLength,
    pointerOffset,
    progressGaugeRadius,
    throttledWidth,
    titleVerticalCenter,
    valueBoxHeight,
    valueBoxWidth,
    valueBoxOffset,
    unit,
    data.value,
    sanitizedLabel,
    chartsTheme.noDataOption,
    chartsTheme.echartsTheme.textStyle?.color,
    progressWidth,
    max,
    valueToDisplay,
    valueColorAxisLine,
    correctedAxisLine,
    showValue,
    valueTruncated,
  ]);

  // Compute the overlay area covering the label text.
  // Gauge center is at 65% of chart height. Label is positioned below center via titleVerticalCenter.
  const labelOverlayTop = Math.min(height * 0.65 + titleVerticalCenter - TITLE_HEIGHT / 2, height - TITLE_HEIGHT);
  const labelOverlayWidth = throttledWidth * 0.8;
  const labelOverlayLeft = (width - labelOverlayWidth) / 2;

  return (
    <div style={{ width: `${width}px`, height: `${height}px`, position: 'relative' }}>
      <EChart width={width} height={height} option={option} theme={chartsTheme.echartsTheme} />
      <div
        style={{
          position: 'absolute',
          top: messageBoxAbsoluteY,
          left: messageBoxAbsoluteX,
          width: `${messageBoxWidth}px`,
          height: `${messageBoxHeight}px`,
        }}
      >
        <FillTextSpace
          height={messageBoxHeight}
          width={messageBoxWidth}
          minFontSize={8}
          maxFontSize={showValue ? valueBoxHeight * 0.8 : undefined}
          maxLines={showValue ? 2 : 3}
          horizontallyCenterText={true}
          verticallyCenterText={showValue ? false : true}
          color={textColor}
          padding={0}
        >
          {messageToDisplay}
        </FillTextSpace>
      </div>
      <Tooltip
        Content={tooltipContent}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'background.popover',
              color: 'text.primary',
            },
          },
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: labelOverlayTop,
            left: labelOverlayLeft,
            width: labelOverlayWidth,
            height: TITLE_HEIGHT,
          }}
        />
      </Tooltip>
    </div>
  );
}

export type GaugeSizeVariant = 'small' | 'extra-small' | 'default';

export function getGaugeSizeVariant(dimension: number): GaugeSizeVariant {
  if (dimension <= GAUGE_EXTRA_SMALL_BREAKPOINT) return 'extra-small';
  if (dimension <= GAUGE_SMALL_BREAKPOINT) return 'small';
  return 'default';
}

function calculateGaugeLayout({
  width,
  height,
  value,
  unit,
  showValue,
  panwRebrandStubYear,
}: {
  width: number;
  height: number;
  value: number | null;
  unit: UnitOptions;
  showValue: boolean;
  panwRebrandStubYear: boolean;
}) {
  const dimension = Math.min(width, height);
  const gaugeSize = getGaugeSizeVariant(dimension);

  // Max radius size is 1/2 of the square the chart is rendered in
  const radius = dimension / 2;
  const thresholdsRadius = radius;

  const breakpointSpan = GAUGE_MAX_BREAKPOINT - GAUGE_SMALL_BREAKPOINT;

  const breakpointPercent =
    gaugeSize !== 'default' ? 0 : Math.min((dimension - GAUGE_SMALL_BREAKPOINT) / breakpointSpan, 1);

  let progressWidth = Math.floor(MIN_PROGRESS_WIDTH + (MAX_PROGRESS_WIDTH - MIN_PROGRESS_WIDTH) * breakpointPercent);
  if (gaugeSize === 'small') {
    // Use a smaller progress width for small gauges
    progressWidth = Math.floor(progressWidth * 0.5);
  }

  // We need to add 15% of the height to everything that is positioned outside of the actual chart
  const heightOffset = 0.15 * height;

  // The title for the chart is offset by 64% of the radius
  // This is just a random number that puts it in the place that seems right
  const titleVerticalCenter = 0.64 * 0.86 * radius;

  // Threshold arc is only shown on the default size gauge
  const progressGaugeRadius =
    gaugeSize === 'default'
      ? thresholdsRadius - THRESHOLDS_LINE_WIDTH - SPACING_BETWEEN_THRESHOLDS_AND_PROGRESS
      : thresholdsRadius;

  const pointerLength = Math.floor(MIN_POINTER_LENGTH + (MAX_POINTER_LENGTH - MIN_POINTER_LENGTH) * breakpointPercent);

  // This is radius for the arc that the pointer moves around.
  // pointer is positioned with the end opposite the point positioned on this arc which is
  // why we have to subtract the pointer length
  let pointerOffset = progressGaugeRadius - progressWidth - SPACING_BETWEEN_PROGRESS_AND_POINTER - pointerLength;

  if (gaugeSize === 'extra-small') {
    // For extra-small gauges, we will not show this icon and use most of the radius for text
    pointerOffset = progressGaugeRadius * 0.85;
  }
  // The ideal text box inside of the gauge is the square inscribed inside the
  // the pointer circle.  The formula sqrt(2) * radius gives us the width of that square
  const inscribedSquareWidth = Math.floor(Math.sqrt(2) * pointerOffset);

  // For small gauges where the container is wider than the gauge circle,
  // allow the value text to use the extra horizontal space
  const valueBoxWidth =
    gaugeSize === 'extra-small' ? Math.max(inscribedSquareWidth, Math.floor(width * 0.8)) : inscribedSquareWidth;

  // Our "target" value message box height is 2/3 of the square.
  // This leaves 1/3 + slightly more for the message
  const oneThirdWidth = Math.floor(inscribedSquareWidth / 3);
  let optimalTextBoxHeight = oneThirdWidth * 2;

  // For small gauges, cap value height to leave room for the message area above the label
  if (gaugeSize === 'extra-small') {
    const messageAreaBottom = titleVerticalCenter - TITLE_HEIGHT / 2 - SPACING_BETWEEN_MESSAGE_AND_TITLE;
    const maxValueHeight = messageAreaBottom + inscribedSquareWidth / 2 - MIN_VALUE_FONT_SIZE;
    optimalTextBoxHeight = Math.min(optimalTextBoxHeight, Math.max(MIN_VALUE_FONT_SIZE, maxValueHeight));
  }

  // Push value text up within the inscribed square; offset adapts when height is capped for small gauges
  const valueBoxOffset = inscribedSquareWidth / 2 - optimalTextBoxHeight / 2;

  // Now that we have our ideal text box dimensions, we find the text size that actually fits in it
  // we can then adjust the box size to give the message as much space as possible
  const unclampedFontSize = findOptimalFontSize(value, unit, valueBoxWidth, optimalTextBoxHeight, panwRebrandStubYear);
  const valueFontSize = Math.max(unclampedFontSize, MIN_VALUE_FONT_SIZE);
  const valueTruncated = unclampedFontSize < MIN_VALUE_FONT_SIZE;
  const valueBoxHeight = valueFontSize;

  // Figure out where the messageBox can start.
  // It should be half of the actual text box height below the text box offset
  const messageBoxTop = valueBoxHeight / 2 - valueBoxOffset;
  const messageBoxBottom = titleVerticalCenter - TITLE_HEIGHT / 2 - SPACING_BETWEEN_MESSAGE_AND_TITLE;
  let optimalMessageBoxHeight = messageBoxBottom - messageBoxTop;

  // The message box can be wider than the value text box because of where it falls on the circle
  // It's width can be the width across from the 2 end points of the gauge
  // This is the formula for finding the length of a chord of a circle. 2 * r * sin(angle/2)
  const gaugeMessageBoxWidth = 2 * pointerOffset * Math.sin(Math.PI / 3);
  const messageBoxWidth =
    gaugeSize === 'extra-small' ? Math.max(gaugeMessageBoxWidth, Math.floor(width * 0.8)) : gaugeMessageBoxWidth;

  // Absolute position for the message box will be the center - half the width
  let messageBoxAbsoluteX = width / 2 - messageBoxWidth / 2;

  // Distance from pointerOffset to the top of the optimal box
  const pointerToOptimalBoxTopDistance = pointerOffset - inscribedSquareWidth / 2;

  // Extra space from the diff between dimension and height
  // Half of this needs to be added onto the messageBoxAbsoluteY
  const extraVerticalSpace = Math.floor((height - dimension) / 2);

  // radius - pointeroffset gives distance from outer radius to pointer offset radius
  // pointerToOptimalBoxTopDistance
  // optimaltextboxheight
  // take away half the difference between optimal and actual
  let messageBoxAbsoluteY =
    extraVerticalSpace +
    radius -
    pointerOffset +
    pointerToOptimalBoxTopDistance +
    optimalTextBoxHeight -
    (optimalTextBoxHeight - valueBoxHeight) / 2;

  if (!showValue) {
    // Special case. We are only showing the message
    // The message gets the optimal value text box space
    optimalMessageBoxHeight = optimalTextBoxHeight;
    messageBoxAbsoluteX = width / 2 - messageBoxWidth / 2;
    messageBoxAbsoluteY = extraVerticalSpace + radius - pointerOffset + pointerToOptimalBoxTopDistance;
  }

  return {
    gaugeSize,
    messageBoxHeight: optimalMessageBoxHeight,
    messageBoxWidth,
    messageBoxAbsoluteX,
    messageBoxAbsoluteY: messageBoxAbsoluteY + heightOffset,
    pointerLength,
    pointerOffset,
    progressGaugeRadius,
    progressWidth,
    radius,
    titleVerticalCenter,
    valueBoxHeight,
    valueBoxWidth,
    valueBoxOffset,
    valueTruncated,
  };
}

function findOptimalFontSize(
  value: number | string | null,
  unit: UnitOptions,
  width: number,
  height: number,
  panwRebrandStubYear: boolean
) {
  const formattedValue = typeof value === 'number' ? formatValue(value, unit) : `${value}`;

  const fontSize = 12;
  const fontFamily = panwRebrandStubYear ? unityTokens['--font-family-sans'] : 'Inter';

  // set the font on the canvas context
  const fontStyle = `${400} ${fontSize}px ${fontFamily}`;

  const textMetrics = measureText(formattedValue, fontStyle);

  if (!textMetrics) {
    return 12;
  }

  // check how much bigger we can make the font while staying within the width and height
  const fontSizeBasedOnWidth = (width / textMetrics.width) * fontSize;
  const fontSizeBasedOnHeight = height;

  // return the smaller font size
  const finalFontSize = Math.min(fontSizeBasedOnHeight, fontSizeBasedOnWidth);
  return finalFontSize;
}
